import { PauseCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { PauseBookingDialog } from "@/domains/settings/features/online/components/pause-booking-dialog";
import { usePauseOnlineBooking } from "@/domains/settings/features/online/hooks/use-pause-online-booking";
import { cn } from "@/lib/utils";

interface PauseBookingsButtonProps {
  isMobile?: boolean;
}

/**
 * Calendar-header quick action to pause new online bookings. Reuses the shared
 * pause dialog + hook. When bookings are already paused the button turns amber
 * and opens the dialog in status mode, where the owner can resume or change the
 * pause. One-click resuming also stays available via the global paused banner.
 */
export function PauseBookingsButton({ isMobile = false }: PauseBookingsButtonProps) {
  const {
    isPaused,
    isIndefinite,
    pausedUntil,
    pausedMessage,
    salonTimezone,
    saving,
    pause,
    resume,
  } = usePauseOnlineBooking();
  const { t } = useTranslation("settings");
  const [dialogOpen, setDialogOpen] = useState(false);
  const label = isPaused ? t("pauseBookings.statusTitle") : t("pauseBookings.pauseTitle");

  return (
    <>
      <Button
        variant="ghost"
        size={isMobile ? "icon" : "sm"}
        onClick={() => setDialogOpen(true)}
        className={cn(
          isMobile ? "h-10 w-10" : "h-9",
          isPaused &&
            (isMobile
              ? "bg-amber-100 text-amber-700 hover:bg-amber-200 hover:text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60 dark:hover:text-amber-200"
              : "text-amber-700 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300")
        )}
        title={label}
        aria-label={label}
      >
        <PauseCircle className={isMobile ? "w-5 h-5" : "w-4 h-4 mr-2"} />
        {!isMobile && (isPaused ? t("pauseBookings.paused") : t("pauseBookings.pause"))}
      </Button>
      <PauseBookingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        saving={saving}
        onConfirm={pause}
        isPaused={isPaused}
        isIndefinite={isIndefinite}
        pausedUntil={pausedUntil}
        pausedMessage={pausedMessage}
        salonTimezone={salonTimezone}
        onResume={resume}
      />
    </>
  );
}
